import { useMemo, useState } from 'react';
import { Button, Card, Col, Form, Modal, Nav, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import LoadingContainer from 'components/common/LoadingContainer';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import CreateReminderModal from 'components/dashboards/CreateReminderModal';
import { useAuthContext } from 'context/authContext';
import {
  useReminders,
  useCompleteReminder,
  useDeleteReminder,
  useReopenReminder,
  useOrganisationUsers
} from 'hooks/useReminders';
import paths from 'routes/paths';

const TYPE_LABELS = {
  follow_up: 'Follow Up',
  payment: 'Payment',
  meeting: 'Meeting',
  task: 'Task',
  update: 'Update'
};
const TYPE_COLORS = {
  follow_up: 'info',
  payment: 'success',
  meeting: 'primary',
  task: 'secondary',
  update: 'warning'
};

const entityLink = r => {
  if (!r.entity_id) return null;
  const path =
    r.entity_type === 'donor'
      ? paths.donorProfile.replace(':id', r.entity_id)
      : paths.beneficiaryProfile.replace(':id', r.entity_id);
  return (
    <Link to={path} className="fs--1">
      {r.entity_name || (r.entity_type === 'donor' ? 'Donor' : 'Beneficiary')}
    </Link>
  );
};

const ReopenModal = ({ reminder, users, onHide, onConfirm, busy }) => {
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState(reminder?.assigned_to_user_id || '');
  if (!reminder) return null;
  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Reopen reminder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>{reminder.title}</strong> goes back to open. Say what still
          needs doing and who should do it.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Note for the assignee</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Donor wasn't actually called - please phone them and log the outcome"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Assign to</Form.Label>
          <Form.Select
            value={assignee}
            onChange={e => setAssignee(e.target.value)}
          >
            <option value="">Keep current assignee</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="warning"
          disabled={busy}
          onClick={() =>
            onConfirm({ note, assignedToUserId: assignee || undefined })
          }
        >
          Reopen
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Manage all reminders (#32): open / overdue / completed, complete, reopen +
 * reassign, edit, delete. Staff see their own; managers see the branch.
 */
const RemindersPage = () => {
  const { user } = useAuthContext();
  const { reminders, isLoading } = useReminders({});
  const { users } = useOrganisationUsers();
  const { completeReminder, isCompleting } = useCompleteReminder();
  const { deleteReminder, isDeleting } = useDeleteReminder();
  const { reopenReminder, isReopening } = useReopenReminder();

  const [view, setView] = useState('open');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [reopening, setReopening] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [mobileSearch, setMobileSearch] = useState('');

  const today = dayjs().startOf('day');
  const counts = useMemo(
    () => ({
      open: reminders.filter(
        r => r.status === 'pending' && !dayjs(r.due_date).isBefore(today)
      ).length,
      overdue: reminders.filter(
        r => r.status === 'pending' && dayjs(r.due_date).isBefore(today)
      ).length,
      completed: reminders.filter(r => r.status === 'completed').length
    }),
    [reminders, today]
  );

  const tableData = useMemo(
    () =>
      reminders
        .filter(r => {
          const overdue =
            r.status === 'pending' && dayjs(r.due_date).isBefore(today);
          if (view === 'open' && !(r.status === 'pending' && !overdue))
            return false;
          if (view === 'overdue' && !overdue) return false;
          if (view === 'completed' && r.status !== 'completed') return false;
          if (assigneeFilter && r.assigned_to_user_id !== assigneeFilter)
            return false;
          if (typeFilter && r.reminder_type !== typeFilter) return false;
          return true;
        })
        .map(r => ({
          ...r,
          assignedName: r.assigned_to
            ? `${r.assigned_to.first_name} ${r.assigned_to.last_name}`
            : '—',
          createdName: r.created_by
            ? `${r.created_by.first_name} ${r.created_by.last_name}`
            : '—',
          dueSort: `${r.due_date} ${r.due_time || ''}`
        })),
    [reminders, view, assigneeFilter, typeFilter, today]
  );

  const columns = [
    {
      accessor: 'title',
      Header: 'Reminder',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const r = row.original;
        return (
          <div>
            <div className="fw-semi-bold">{r.title}</div>
            {r.description && (
              <div
                className="text-600 fs--1"
                style={{ whiteSpace: 'normal', maxWidth: 420 }}
              >
                {r.description}
              </div>
            )}
            {r.reopen_note && (
              <div
                className="text-warning fs--1 fst-italic"
                style={{ whiteSpace: 'normal', maxWidth: 420 }}
              >
                Reopened: {r.reopen_note}
              </div>
            )}
          </div>
        );
      }
    },
    {
      accessor: 'reminder_type',
      Header: 'Type',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => (
        <SoftBadge pill bg={TYPE_COLORS[value] || 'secondary'}>
          {TYPE_LABELS[value] || value}
        </SoftBadge>
      )
    },
    {
      accessor: 'dueSort',
      Header: 'Due',
      cellProps: { className: 'py-2 text-nowrap' },
      Cell: ({ row }) => {
        const r = row.original;
        const overdue =
          r.status === 'pending' && dayjs(r.due_date).isBefore(today);
        return (
          <span className={overdue ? 'text-danger fw-semi-bold' : ''}>
            {dayjs(r.due_date).format('DD MMM YYYY')}
            {r.due_time && (
              <span className="text-600">
                {' '}
                {String(r.due_time).slice(0, 5)}
              </span>
            )}
            {r.repeat_rule && r.repeat_rule !== 'none' && (
              <SoftBadge pill bg="info" className="ms-2 fs--2">
                {r.repeat_rule}
              </SoftBadge>
            )}
          </span>
        );
      }
    },
    {
      accessor: 'entity_name',
      Header: 'Linked to',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) =>
        entityLink(row.original) || <span className="text-500">—</span>
    },
    {
      accessor: 'assignedName',
      Header: 'Assigned to',
      cellProps: { className: 'py-2' }
    },
    {
      accessor: 'createdName',
      Header: 'Set by',
      cellProps: { className: 'py-2' }
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const r = row.original;
        if (r.status === 'completed') {
          return (
            <div>
              <SoftBadge pill bg="success">
                Completed
              </SoftBadge>
              <div className="text-600 fs--2">
                {r.completed_at &&
                  dayjs(r.completed_at).format('DD/MM/YY HH:mm')}
              </div>
            </div>
          );
        }
        const overdue = dayjs(r.due_date).isBefore(today);
        return (
          <SoftBadge pill bg={overdue ? 'danger' : 'warning'}>
            {overdue ? 'Overdue' : 'Open'}
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'actions',
      Header: '',
      disableSortBy: true,
      cellProps: { className: 'py-2 text-end text-nowrap' },
      Cell: ({ row }) => {
        const r = row.original;
        const mine = r.created_by_user_id === user?.id;
        return (
          <div className="d-flex gap-1 justify-content-end">
            {r.status === 'pending' ? (
              <>
                <Button
                  size="sm"
                  variant="falcon-default"
                  className="px-2 py-1"
                  title="Edit"
                  onClick={() => {
                    setEditing(r);
                    setShowCreate(true);
                  }}
                >
                  <FontAwesomeIcon icon="edit" className="fs--1" />
                </Button>
                <Button
                  size="sm"
                  variant="falcon-success"
                  className="px-2 py-1"
                  title="Complete"
                  disabled={isCompleting}
                  onClick={() => completeReminder(r.id)}
                >
                  <FontAwesomeIcon icon="check" className="fs--1" />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="falcon-warning"
                className="px-2 py-1"
                title="Reopen and reassign"
                onClick={() => setReopening(r)}
              >
                <FontAwesomeIcon icon="redo" className="fs--1 me-1" /> Reopen
              </Button>
            )}
            {mine && (
              <Button
                size="sm"
                variant="falcon-danger"
                className="px-2 py-1"
                title="Delete"
                disabled={isDeleting}
                onClick={() => setDeletingId(r.id)}
              >
                <FontAwesomeIcon icon="trash" className="fs--1" />
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  // Phones get a card per reminder: the table's eight columns only fit by
  // scrolling sideways, which hides the due date, status and actions
  const term = mobileSearch.trim().toLowerCase();
  const mobileRows = tableData
    .filter(
      r =>
        !term ||
        `${r.title} ${r.description || ''} ${r.entity_name || ''} ${
          r.assignedName
        }`
          .toLowerCase()
          .includes(term)
    )
    .sort((a, b) => a.dueSort.localeCompare(b.dueSort));
  const cell = (accessor, r) =>
    columns
      .find(c => c.accessor === accessor)
      .Cell({ row: { original: r }, value: r[accessor] });

  if (isLoading) return <LoadingContainer />;

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          <Flex
            justifyContent="between"
            alignItems="center"
            className="flex-wrap gap-2"
          >
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon="bell" className="me-2" />
                Reminders &amp; tasks
              </h5>
              <div className="fs--1 text-600">
                <span className="text-danger fw-semi-bold">
                  {counts.overdue} overdue
                </span>{' '}
                · {counts.open} open · {counts.completed} completed
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditing(null);
                setShowCreate(true);
              }}
            >
              <FontAwesomeIcon icon="plus" className="me-1" /> New reminder
            </Button>
          </Flex>
        </Card.Header>
        <Card.Body className="pt-0">
          <Nav variant="tabs" className="mb-3 flex-nowrap">
            {[
              ['open', 'Open', counts.open, 'primary'],
              ['overdue', 'Overdue', counts.overdue, 'danger'],
              ['completed', 'Completed', counts.completed, 'secondary']
            ].map(([key, label, n, bg]) => (
              <Nav.Item key={key}>
                <Nav.Link
                  active={view === key}
                  onClick={() => setView(key)}
                  className="px-2 px-sm-3 text-nowrap"
                >
                  {label}{' '}
                  <SoftBadge pill bg={bg} className="ms-1">
                    {n}
                  </SoftBadge>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <Row className="g-2">
            <Col xs={6} md={4}>
              <Form.Select
                size="sm"
                value={assigneeFilter}
                onChange={e => setAssigneeFilter(e.target.value)}
              >
                <option value="">Everyone</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                    {u.id === user?.id ? ' (me)' : ''}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={4}>
              <Form.Select
                size="sm"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
              >
                <option value="">All types</option>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-none d-md-block">
        <CustomAdvanceTable
          data={tableData}
          columns={columns}
          title={
            view === 'completed'
              ? 'Completed reminders'
              : view === 'overdue'
              ? 'Overdue reminders'
              : 'Open reminders'
          }
          showSearchInput
          searchInputPlaceHolder="Search reminders..."
          addNew={false}
          loading={isLoading}
          perPage={25}
        />
      </div>

      <Card className="d-md-none">
        <Card.Header className="pb-2">
          <Form.Control
            size="sm"
            placeholder="Search reminders..."
            value={mobileSearch}
            onChange={e => setMobileSearch(e.target.value)}
          />
        </Card.Header>
        <Card.Body className="p-0">
          {mobileRows.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No {view} reminders{term && ' match this search'}
            </div>
          ) : (
            mobileRows.map(r => (
              <div key={r.id} className="px-3 py-3 border-top">
                <Flex
                  justifyContent="between"
                  alignItems="start"
                  className="gap-2"
                >
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    {cell('title', r)}
                  </div>
                  <div className="flex-shrink-0">{cell('actions', r)}</div>
                </Flex>
                <div className="d-flex align-items-center flex-wrap gap-2 mt-2 fs--1">
                  {cell('status', r)}
                  {cell('reminder_type', r)}
                  {cell('dueSort', r)}
                </div>
                <div className="text-600 fs--1 mt-2">
                  {r.entity_id && <>Linked to {entityLink(r)} · </>}
                  Assigned to {r.assignedName} · Set by {r.createdName}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      <CreateReminderModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        editingReminder={editing}
      />
      {reopening && (
        <ReopenModal
          reminder={reopening}
          users={users}
          busy={isReopening}
          onHide={() => setReopening(null)}
          onConfirm={args =>
            reopenReminder(
              { id: reopening.id, ...args },
              { onSuccess: () => setReopening(null) }
            )
          }
        />
      )}
      <ConfirmationModal
        show={!!deletingId}
        onHide={() => setDeletingId(null)}
        title="Delete reminder"
        body="Delete this reminder? This cannot be undone."
        handleConfirm={() => {
          deleteReminder(deletingId);
          setDeletingId(null);
        }}
      />
    </>
  );
};

export default RemindersPage;
